import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

/**
 * Higher-Order Component that provides React Router v6+ hooks to class components
 * This allows class components to access useParams, useLocation, and useNavigate
 * by wrapping them with this HOC.
 * 
 * Usage:
 * export default withRouterParams(YourClassComponent);
 * 
 * The wrapped component will receive these props:
 * - match: { params: {...} } (for backward compatibility)
 * - location: location object
 * - navigate: navigation function
 * - history: history object (mimics React Router v5 history)
 */
export function withRouterParams(Component) {
    function ComponentWithRouterProp(props) {
        const params = useParams();
        const location = useLocation();
        const navigate = useNavigate();
        
        // Create a match object similar to React Router v5 for backward compatibility
        const match = {
            params
        };

        // Create a history object similar to React Router v5
        const history = {
            push: (to) => navigate(to),
            replace: (to) => navigate(to, { replace: true }),
            goBack: () => navigate(-1),
            goForward: () => navigate(1),
            location,
            listen: () => () => {}, // Noop function that returns cleanup function
            block: () => () => {}, // Noop function that returns cleanup function
            createHref: (to) => typeof to === 'string' ? to : to.pathname
        };

        return (
            <Component
                {...props}
                match={match}
                location={location}
                navigate={navigate}
                history={history}
            />
        );
    }

    ComponentWithRouterProp.displayName = `withRouterParams(${Component.displayName || Component.name})`;
    
    return ComponentWithRouterProp;
} 